import React, { useRef, useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useFinancial } from '../../context/FinancialContext';
import { stateToCSV, csvToState } from '../../utils/csvHelper';
import RoadmapWalkthroughModal from '../modals/roadmap/RoadmapWalkthroughModal';

const { FiShield, FiUpload, FiFileText, FiMap, FiCheck } = FiIcons;

const Header = ({ onOpenWalkthrough }) => {
  const { state, dispatch } = useFinancial();
  const fileInputRef = useRef(null);
  const [showInternalWalkthrough, setShowInternalWalkthrough] = useState(false);
  const [importStatus, setImportStatus] = useState(null);

  // Local state for formatted Invested Capital input
  const [localNetWorth, setLocalNetWorth] = useState(
    state.goals.startingNetWorth != null ? Number(state.goals.startingNetWorth).toLocaleString('en-US') : ''
  );

  useEffect(() => {
    setLocalNetWorth(
      state.goals.startingNetWorth != null ? Number(state.goals.startingNetWorth).toLocaleString('en-US') : ''
    );
  }, [state.goals.startingNetWorth]);

  const handleNetWorthChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9]/g, '');
    const formattedValue = rawValue ? parseInt(rawValue, 10).toLocaleString('en-US') : '';
    setLocalNetWorth(formattedValue);
    const num = parseFloat(rawValue);
    dispatch({ type: 'UPDATE_GOALS', payload: { startingNetWorth: isNaN(num) ? 0 : num } });
  };

  const handleExport = () => {
    try {
      const csvContent = stateToCSV(state);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `WEALTH_Export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        if (typeof text === 'string') {
          const importedData = csvToState(text);
          if (importedData) {
            dispatch({ type: 'IMPORT_DATA', payload: importedData });
            setImportStatus('success');
            setTimeout(() => setImportStatus(null), 3000);
          }
        }
      } catch (err) {
        console.error('CSV import parsing error:', err);
      } finally {
        // Reset file input so re-uploading the same file triggers onChange
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };
    reader.readAsText(file);
  };

  const openGuide = () => {
    if (onOpenWalkthrough) {
      onOpenWalkthrough();
    } else {
      setShowInternalWalkthrough(true);
    }
  };

  const goalsList = [
    { label: 'Current Age', key: 'currentAge' },
    { label: 'Target Age', key: 'targetAge' },
    { label: 'Expected ROI %', key: 'expectedROI' },
    { label: 'Invested Capital', key: 'startingNetWorth' }
  ];

  const renderInput = (goal) =>
    goal.key === 'startingNetWorth' ? (
      <input
        type="text"
        inputMode="numeric"
        aria-label="Invested Capital"
        className="bg-transparent border-none p-0 text-xs font-black text-slate-900 focus:ring-0 w-full outline-none"
        value={localNetWorth}
        onChange={handleNetWorthChange}
      />
    ) : (
      <input
        type="number"
        aria-label={goal.label}
        className="bg-transparent border-none p-0 text-xs font-black text-slate-900 focus:ring-0 w-full outline-none"
        value={state.goals[goal.key] ?? ''}
        onChange={(e) => {
          dispatch({ type: 'UPDATE_GOALS', payload: { [goal.key]: parseFloat(e.target.value) || 0 } });
        }}
      />
    );

  return (
    <>
      {/* Main Header - Sticky on Desktop and Brand sticky on Mobile */}
      <header className="bg-white sticky top-0 z-[200] shadow-sm shadow-slate-100/50 border-b border-slate-50">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-3.5 flex items-center justify-between gap-4 lg:gap-8">
          {/* Brand Section */}
          <div className="flex items-center justify-between w-full lg:w-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white shadow-lg">
                <SafeIcon icon={FiShield} className="text-xl" />
              </div>
              <div className="text-left">
                <h1 className="text-sm font-black text-slate-900 leading-tight tracking-tight uppercase">WEALTH</h1>
                <p className="text-[8px] font-black uppercase text-slate-400 tracking-widest">Financial Roadmap</p>
              </div>
            </div>

            {/* Mobile Actions */}
            <div className="flex lg:hidden items-center gap-2">
              <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleImport} />
              <button
                onClick={openGuide}
                className="p-2.5 bg-blue-50 text-blue-600 rounded-xl active:bg-blue-100"
                title="Roadmap Guide"
                aria-label="Roadmap Guide"
              >
                <SafeIcon icon={FiMap} />
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-2.5 bg-slate-100 text-slate-600 rounded-xl active:bg-slate-200"
                title="Import CSV"
                aria-label="Import CSV"
              >
                <SafeIcon icon={importStatus === 'success' ? FiCheck : FiUpload} className={importStatus === 'success' ? 'text-emerald-600' : ''} />
              </button>
              <button
                onClick={handleExport}
                className="p-2.5 bg-slate-900 text-white rounded-xl active:bg-black"
                title="Export CSV"
                aria-label="Export CSV"
              >
                <SafeIcon icon={FiFileText} />
              </button>
            </div>
          </div>

          {/* Desktop Inputs */}
          <div className="hidden lg:flex flex-1 items-center">
            {goalsList.map((goal, i) => (
              <div key={goal.key} className={`flex flex-col text-left ${i > 0 ? 'border-l border-slate-100 pl-6' : ''}`}>
                <span className="text-[8px] font-black uppercase text-slate-400 tracking-widest mb-1 whitespace-nowrap">
                  {goal.label}
                </span>
                <div className="bg-slate-50 rounded-lg px-2.5 py-1 mr-6 hover:bg-slate-100/80 transition-colors">
                  {renderInput(goal)}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-2">
            <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleImport} />
            <button
              onClick={openGuide}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-blue-100 transition-all border border-blue-100"
            >
              <SafeIcon icon={FiMap} /> Roadmap Guide
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 text-slate-600 text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-slate-200 transition-all border border-slate-200"
            >
              <SafeIcon icon={importStatus === 'success' ? FiCheck : FiUpload} className={importStatus === 'success' ? 'text-emerald-600' : ''} />
              {importStatus === 'success' ? 'Imported' : 'Import'}
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-xl shadow-slate-200 hover:bg-black transition-all"
            >
              <SafeIcon icon={FiFileText} /> Export
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Inputs - Scrolls with the page */}
      <div className="lg:hidden bg-white px-4 py-4 border-b border-slate-100 shadow-sm relative z-[190]">
        <div className="grid grid-cols-2 gap-y-4">
          {goalsList.map((goal, i) => (
            <div key={goal.key} className={`flex flex-col text-left ${i === 1 || i === 3 ? 'border-l border-slate-100 pl-4' : ''}`}>
              <span className="text-[8px] font-black uppercase text-slate-400 tracking-widest mb-1 whitespace-nowrap">
                {goal.label}
              </span>
              <div className="bg-slate-50 rounded-lg px-2.5 py-1 mr-4">
                {renderInput(goal)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {showInternalWalkthrough && (
        <RoadmapWalkthroughModal isOpen={showInternalWalkthrough} onClose={() => setShowInternalWalkthrough(false)} />
      )}
    </>
  );
};

export default Header;